import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle, Circle, ArrowSquareOut, Clock } from "@phosphor-icons/react";

const CATEGORY_ORDER = ["AI", "Automation", "Messaging", "Payments", "Data"];

const STATUS_BADGE = {
  connected:      { label: "CONNECTED",     bg: "#E4F5EA", fg: "#1E7C3B", bd: "#34C759" },
  disabled:       { label: "DISABLED",      bg: "#FFF5DB", fg: "#946200", bd: "#F5C34A" },
  not_configured: { label: "NOT CONFIGURED", bg: "#F4F6F8", fg: "#525252", bd: "#E5E5E5" },
  error:          { label: "ERROR",         bg: "#FDECEC", fg: "#B4232C", bd: "#EF6767" },
  coming_later:   { label: "COMING LATER",  bg: "#EEF2FA", fg: "#25467A", bd: "#94ABD8" },
};

function derivedStatus(provider, connectionForProvider) {
  if (provider.coming_later) return "coming_later";
  if (!provider.server_configured && provider.server_configured !== undefined) return "not_configured";
  if (!connectionForProvider) return "not_configured";
  if (connectionForProvider.status === "error") return "error";
  if (connectionForProvider.is_enabled && provider.server_enabled && provider.server_configured) return "connected";
  return "disabled";
}

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.not_configured;
  return (
    <span className="fx-badge" data-testid={`integration-status-${status}`} style={{
      background: s.bg, color: s.fg, borderColor: s.bd,
    }}>
      {s.label}
    </span>
  );
}

export default function IntegrationsCard() {
  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const [{ data: ps }, { data: cs }] = await Promise.all([
        api.get("/integrations/providers"),
        api.get("/integrations/connections").catch(() => ({ data: [] })),
      ]);
      setProviders(ps || []);
      setConnections(cs || []);
    } catch {
      setProviders([]);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  const grouped = CATEGORY_ORDER
    .map((cat) => ({ cat, list: providers.filter((p) => p.category === cat) }))
    .filter((g) => g.list.length);

  const connByProvider = Object.fromEntries(connections.map((c) => [c.provider, c]));

  return (
    <div className="border border-black/10 p-8" data-testid="integrations-card">
      <div className="fx-mono-tag mb-6">INTEGRATIONS</div>
      <p className="text-sm text-neutral-600 mb-6 max-w-lg">
        Third-party services Agenaix can plug into. n8n is the first supported integration; more
        providers will follow. Configure secrets in the backend environment — never in the browser.
      </p>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : providers.length === 0 ? (
        <div className="text-sm text-neutral-500">No providers registered.</div>
      ) : (
        <div className="space-y-6">
          {grouped.map(({ cat, list }) => (
            <div key={cat}>
              <div className="fx-mono-tag mb-3" style={{ fontSize: "12px" }}>{cat.toUpperCase()}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {list.map((p) => {
                  const conn = connByProvider[p.key];
                  const status = derivedStatus(p, conn);
                  return (
                    <div key={p.key}
                         className="border border-black/10 p-4 rounded-md"
                         data-testid={`integration-${p.key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {status === "connected" ? (
                            <CheckCircle size={16} weight="fill" className="text-[#34C759]" />
                          ) : status === "coming_later" ? (
                            <Clock size={16} weight="duotone" className="text-neutral-400" />
                          ) : (
                            <Circle size={16} weight="duotone" className="text-neutral-400" />
                          )}
                          <span className="font-semibold text-sm tracking-tight">{p.name}</span>
                        </div>
                        <StatusBadge status={status} />
                      </div>
                      <p className="text-xs text-neutral-600 leading-relaxed">{p.description}</p>

                      {p.coming_later && (
                        <div className="mt-3 text-xs text-neutral-500">
                          Not yet available. This provider is on our roadmap.
                        </div>
                      )}

                      {!p.coming_later && (
                        <div className="mt-3 pt-3 border-t border-black/5 space-y-1.5">
                          <div className="text-xs text-neutral-600">
                            Server configured:{" "}
                            <code className="text-xs">{p.server_configured ? "yes" : "no"}</code>
                          </div>
                          <div className="text-xs text-neutral-600">
                            Provider enabled:{" "}
                            <code className="text-xs">{p.server_enabled ? "yes" : "no"}</code>
                          </div>
                          {conn && (
                            <div className="text-xs text-neutral-600">
                              Workspace connection:{" "}
                              <code className="text-xs">{conn.is_enabled ? "enabled" : "disabled"}</code>
                            </div>
                          )}
                        </div>
                      )}

                      <a href={p.docs_url} target="_blank" rel="noreferrer"
                         className="mt-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-[#0B4DCC] hover:underline"
                         data-testid={`integration-docs-${p.key}`}>
                        DOCS <ArrowSquareOut size={11} weight="bold" />
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
